// What the importer had to approximate to get the robot into the scene, shown
// beside the robot rather than only in the console.
//
// Every one of these is a note the user can act on -- a link the URDF gives no
// mass, a mesh that was not in the drop, a joint no servo can drive -- and each
// explains something they would otherwise see as the robot behaving oddly for
// no reason. They still go to the console, where the full text and the browser's
// own formatting are, but a console is not somewhere anyone looks unprompted.
//
// The notes arrive in two batches, because they are learnt in two places: what
// reading the URDF made of it (`RobotPlan.notes`), and what building it from the
// meshes made of it (`AddedRobot.notes`). `showWarnings` opens on the first,
// `addWarnings` appends the second, so the panel is up *before* the robot is
// built -- which is the point of the split. A URDF broken badly enough to bring
// the build down takes wasm with it, and the note explaining why is on screen
// already.

let robotName = "";

function element<T extends HTMLElement>(id: string): T {
  const found = document.getElementById(id);
  if (!found) {
    throw new Error(`index.html has no #${id}`);
  }
  return found as T;
}

// Point the panel at a robot, replacing whatever the last one had to say. A
// robot the importer had no notes about leaves it hidden: an empty panel is a
// claim of its own, and the wrong one, since most robots import cleanly.
export function showWarnings(name: string, notes: string[]) {
  robotName = name;
  const list = element("warnings-list");
  list.replaceChildren();
  wire();
  append(notes);
  render();
}

// The second batch, for the robot `showWarnings` was last called with. Opens the
// panel if reading the URDF had nothing to report but building it did.
export function addWarnings(notes: string[]) {
  append(notes);
  render();
}

// Drop everything: a plan discarded, or a robot that never made it into the
// scene. The notes are about a robot that does not exist, so they go with it.
export function clearWarnings() {
  robotName = "";
  element("warnings-list").replaceChildren();
  render();
}

// The console half is already done, by the Rust that produced the notes:
// `planRobotFromFolder` warns as it reads and `addTo` warns as it builds, both
// at the point the note is made. Repeating it here would double every line.
function append(notes: string[]) {
  const list = element("warnings-list");
  for (const note of notes) {
    const line = document.createElement("div");
    line.className = "warning-note";
    line.textContent = note;
    list.append(line);
  }
}

function render() {
  const panel = element("warnings");
  const list = element("warnings-list");
  const count = list.childElementCount;
  // Named, since a second robot dropped into a running scene leaves the first
  // one's warnings nowhere to be seen -- the panel is about the newest.
  element("warnings-title").textContent =
    count === 0 ? "Warnings" : `Warnings · ${robotName} (${count})`;
  panel.hidden = count === 0;
}

// Dismissal only hides the panel; the notes stay in the list, so nothing has to
// be re-read to put them back. The next robot replaces them either way.
function wire() {
  const close = element<HTMLButtonElement>("warnings-close");
  if (close.dataset.wired === "true") {
    return;
  }
  close.dataset.wired = "true";
  close.addEventListener("click", () => {
    element("warnings").hidden = true;
  });
}
